import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle2, Truck, Clock, Building2, Sparkles } from "lucide-react";
import { CITIES, CITY_LIST } from "../data/cities";
import PriceCalculatorHero from "../components/PriceCalculatorHero";
import useSeo from "../hooks/useSeo";

export default function CityLandingPage({ citySlug }) {
  const params = useParams();
  const slug = citySlug || params.slug;
  const city = CITIES[slug];

  useSeo({
    title: city ? `Kurer ${city.name} · Same-day kurérservice fra A til B · EMA Kurer ApS` : "EMA Kurer",
    description: city
      ? `Brug for en kurer i ${city.name}? EMA Kurer henter direkte hos dig og kører direkte til modtageren. Fra 199 kr inkl. 8 km. Same-day, express og aftenlevering. Book online.`
      : "EMA Kurer ApS — direkte kurérservice fra A til B på Sjælland.",
    path: city ? `/kurer-${city.slug}` : "/",
  });

  // hreflang alternates — link DA city to EN equivalent for international SEO
  const enSlugMap = { koebenhavn: "copenhagen", roskilde: "roskilde", helsingoer: "helsingor" };
  React.useEffect(() => {
    if (!city) return;
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const altDa = document.createElement("link");
    altDa.setAttribute("rel", "alternate");
    altDa.setAttribute("hreflang", "da");
    altDa.setAttribute("href", `https://emakurer.dk/kurer-${city.slug}`);
    document.head.appendChild(altDa);
    if (enSlugMap[city.slug]) {
      const altEn = document.createElement("link");
      altEn.setAttribute("rel", "alternate");
      altEn.setAttribute("hreflang", "en");
      altEn.setAttribute("href", `https://emakurer.dk/en/courier-${enSlugMap[city.slug]}`);
      document.head.appendChild(altEn);
    }
    const altDefault = document.createElement("link");
    altDefault.setAttribute("rel", "alternate");
    altDefault.setAttribute("hreflang", "x-default");
    altDefault.setAttribute("href", `https://emakurer.dk/kurer-${city.slug}`);
    document.head.appendChild(altDefault);
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  // FAQ structured data — major SEO win, eligible for rich snippets
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: city.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const cityLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Kurérservice i ${city.name}`,
    description: city.intro,
    provider: { "@id": "https://emakurer.dk/#business" },
    areaServed: { "@type": "City", name: city.nameLong, addressCountry: "DK" },
    serviceType: "Kurérservice",
    offers: {
      "@type": "Offer",
      price: "199",
      priceCurrency: "DKK",
      description: `Same-day kurér i ${city.name} fra 199 kr inkl. 8 km.`,
    },
  };

  return (
    <div data-testid={`city-page-${city.slug}`}>
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(cityLd) }} />

      {/* HERO */}
      <section className="bg-brand-dark text-white pt-16 pb-12 lg:pt-20 lg:pb-16" data-testid="city-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 text-white/80 bg-white/5 border border-white/10 backdrop-blur-md px-4 py-2" style={{ color: "#fca5a5" }}>
            <Sparkles size={14} />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold">Kurer · {city.name} · Direkte A → B</span>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.05]">
            {city.headline}.<br/>
            <span className="text-brand-red">Direkte fra A til B.</span>
          </h1>
          <p className="text-white/80 text-lg max-w-3xl mt-6 leading-relaxed">{city.tagline}</p>

          {/* Price calculator pre-filled (only addresses change per city, component is reusable) */}
          <div className="mt-10 max-w-4xl">
            <PriceCalculatorHero />
          </div>
        </div>
      </section>

      {/* INTRO + USE CASES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20" data-testid="city-intro">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2">
            <div className="kicker">EMA Kurer i {city.name}</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl mt-3 text-brand-dark tracking-tight">
              Din lokale kurér fra A til B i {city.name}.
            </h2>
            <p className="text-brand-text2 mt-5 text-lg leading-relaxed">{city.intro}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
              {city.useCases.map((u) => (
                <div key={u.t} className="bg-white border border-brand p-5">
                  <CheckCircle2 size={20} className="text-brand-red" />
                  <div className="font-display font-semibold text-brand-dark mt-3">{u.t}</div>
                  <p className="text-sm text-brand-text2 mt-2 leading-relaxed">{u.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="bg-brand-bg2 border border-brand p-7" data-testid="city-sidebar">
            <div className="kicker">Hurtige fakta</div>
            <div className="font-display font-semibold text-xl text-brand-dark mt-2">Kurer i {city.name}</div>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-start gap-3"><Truck size={16} className="text-brand-red mt-0.5" /><span><strong>Same-Day</strong> fra 199 kr inkl. 8 km</span></li>
              <li className="flex items-start gap-3"><Clock size={16} className="text-brand-red mt-0.5" /><span><strong>Express</strong> fra 289 kr · 1-2 timer</span></li>
              <li className="flex items-start gap-3"><Clock size={16} className="text-brand-red mt-0.5" /><span><strong>Aftenlevering</strong> 18-22 fra 249 kr</span></li>
              <li className="flex items-start gap-3"><Building2 size={16} className="text-brand-red mt-0.5" /><span><strong>Erhvervskurer</strong> med månedsfaktura</span></li>
            </ul>
            <div className="mt-6 border-t border-brand pt-5">
              <div className="kicker text-xs">Vi dækker i {city.name}</div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {city.deliveryZones.map((z) => (
                  <span key={z} className="text-xs px-2 py-1 bg-white border border-brand">{z}</span>
                ))}
              </div>
            </div>
            <Link to="/book" data-testid="city-cta-book" className="btn-primary w-full justify-center mt-6">
              Book kurer i {city.name} <ArrowRight size={14} />
            </Link>
          </aside>
        </div>
      </section>

      {/* FAQ — gives Google rich snippets */}
      <section className="bg-brand-bg2 border-y border-brand py-16 lg:py-20" data-testid="city-faq">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker">Ofte stillede spørgsmål</div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl mt-3 text-brand-dark tracking-tight">
            FAQ — kurer i {city.name}
          </h2>
          <div className="mt-10 space-y-4">
            {city.faq.map((f, i) => (
              <details key={i} className="bg-white border border-brand p-5 group" data-testid={`city-faq-${i}`}>
                <summary className="font-display font-semibold text-brand-dark cursor-pointer flex items-center justify-between gap-3 list-none">
                  <span>{f.q}</span>
                  <span className="text-brand-red text-2xl font-light group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-brand-text2 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* NEARBY CITIES — internal SEO linking */}
      {city.nearby?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20" data-testid="city-nearby">
          <div className="kicker">Andre byer vi kører i</div>
          <h2 className="font-display font-semibold text-2xl sm:text-3xl mt-3 text-brand-dark tracking-tight">
            Vi leverer også kurérservice i…
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
            {city.nearby.map((s) => {
              const c = CITIES[s];
              if (!c) return null;
              return (
                <Link
                  key={s}
                  to={`/kurer-${s}`}
                  className="group bg-white border border-brand p-5 hover:border-brand-red transition-colors"
                  data-testid={`city-nearby-${s}`}
                >
                  <MapPin size={18} className="text-brand-red" />
                  <div className="font-display font-semibold text-brand-dark mt-3 group-hover:text-brand-red transition-colors">Kurer i {c.name}</div>
                  <div className="text-xs text-brand-text2 mt-1">Fra 199 kr inkl. 8 km</div>
                </Link>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="city-cta-all-services">
              Se alle vores tjenester <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-brand-dark text-white py-16 lg:py-20" data-testid="city-final-cta">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <div className="kicker text-brand-red">Klar?</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight">
            Book din kurer i {city.name} nu.
          </h2>
          <p className="text-white/70 mt-5 max-w-2xl mx-auto">Online booking på 2 minutter. Sikker betaling med kort, MobilePay eller faktura (erhverv).</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book" className="btn-primary" data-testid="city-final-book">
              Book afhentning <ArrowRight size={16} />
            </Link>
            <Link to="/erhverv" className="btn-secondary" data-testid="city-final-business">
              Faste erhvervsaftaler
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Generate a "show all cities" footer block — used in main site footer for SEO internal linking */
export function AllCitiesNav() {
  return (
    <ul className="space-y-2 text-sm" data-testid="footer-cities">
      {CITY_LIST.map((c) => (
        <li key={c.slug}>
          <Link to={`/kurer-${c.slug}`} className="text-white/80 hover:text-white" data-testid={`footer-city-${c.slug}`}>
            Kurer {c.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}
